import { some } from 'lodash-es';

import { Line } from '@atlassian/bitkit-diff';

const CONFLICT_MARKERS = [/^\+<<<<<<<\s/, /^\+>>>>>>>\s/, /^\+=======\s/];

const matchesAnyConflictMarker = (content: string) =>
  some(CONFLICT_MARKERS, marker => marker.test(content));

export type ComparableBlocks = {
  startIndexOfPrevious: number | undefined;
  startIndexofNew: number;
  newText: string;
  previousText: string;
};

const precedingDeletedLines = (chunkChanges: Line[]) => (line: Line) => {
  let index = chunkChanges.indexOf(line);
  index--;
  const result: Line[] = [];
  while (index >= 0 && chunkChanges[index].type === 'del') {
    result.push(chunkChanges[index]);
    index--;
  }
  return result.reverse();
};

const followingNewLines = (chunkChanges: Line[]) => (line: Line) => {
  let index = chunkChanges.indexOf(line);
  index++;
  const result: Line[] = [];
  while (index < chunkChanges.length && chunkChanges[index].type === 'add') {
    result.push(chunkChanges[index]);
    index++;
  }
  return result;
};

const isNotUndefined = (
  c: ComparableBlocks | undefined
): c is ComparableBlocks => {
  return (c as ComparableBlocks) !== undefined;
};

const starterNewLines = (lines: Line[]) =>
  lines.filter((line, idx) => {
    const notFirstLine = idx - 1 >= 0;
    const prevLineIsNotAddition = () => lines[idx - 1].type !== 'add';
    return line.type === 'add' && notFirstLine && prevLineIsNotAddition();
  });

/**
  Find the first added lines in this chunk, collect each block of preceding deletions
  and following additions, join each collection together among themselves to create a
  single resulting pair of strings to be used to produce a word diff later.
  @param allLines Collection of Lines that constitute the changes within a chunk of a diff
  @example
 
  ```
  - var foo = 1;
  - console.log(foo);
  + let foo = 1;
  + console.info(foo);
 
  {
   previousText: 'var foo = 1;\nconsole.log(foo);',
   newText: 'let foo = 1;\nconsole.info(foo);'
  }
  ```
 */
export const segmentize = (allLines: Line[]): ComparableBlocks[] => {
  const conflictIndicators = allLines
    .map(line => line.content)
    .filter(matchesAnyConflictMarker);

  if (conflictIndicators.length > 0) {
    return [];
  }

  const newLinesThatStartNewBlocks = starterNewLines(allLines);

  return newLinesThatStartNewBlocks
    .map(newLine => {
      const prevLines = precedingDeletedLines(allLines)(newLine);
      const newLines = [newLine, ...followingNewLines(allLines)(newLine)];

      if (prevLines.length === 0) {
        return undefined;
      }

      return {
        startIndexOfPrevious: allLines.indexOf(prevLines[0]),
        startIndexofNew: allLines.indexOf(newLines[0]),
        previousText: prevLines.map(line => line.content.substr(1)).join('\n'),
        newText: newLines.map(line => line.content.substr(1)).join('\n'),
      } as ComparableBlocks;
    })
    .filter(isNotUndefined);
};
